import apiClient, { BASE_URL, API_ENDPOINTS } from '../config/api';
import { productsDB, apiCacheDB, syncQueueDB } from '../db/indexedDB';
import syncEngine from '../services/syncEngine';

/**
 * Offline-first API wrapper
 * Tries network first, falls back to IndexedDB cache if offline
 */
class OfflineApi {
  /**
   * GET request with offline support
   */
  async get(endpoint, config = {}) {
    const cacheKey = `${endpoint}:${JSON.stringify(config.params || {})}`;
    
    // If offline, return cached data
    if (!navigator.onLine) {
      console.log('Offline: Returning cached data for', endpoint);
      
      // Check API cache first
      const cached = await apiCacheDB.get(endpoint, 'GET', config.params);
      if (cached) {
        return { data: cached, fromCache: true };
      }
      
      // For specific endpoints, return from IndexedDB
      if (endpoint === API_ENDPOINTS.BRANCH_STOCK || endpoint.includes('/branch-stock')) {
        const products = await productsDB.getAll();
        return { data: products, fromCache: true };
      }
      
      throw new Error('No cached data available and device is offline');
    }
    
    try {
      // Try network request
      const response = await apiClient.get(endpoint, config);
      
      // Cache the response
      await apiCacheDB.set(endpoint, 'GET', config.params, response.data, 60);
      
      // For branch stock, also update IndexedDB
      if (endpoint === API_ENDPOINTS.BRANCH_STOCK || endpoint.includes('/branch-stock')) {
        if (Array.isArray(response.data)) {
          await productsDB.bulkUpsert(response.data);
        }
      }
      
      return response;
    } catch (error) {
      // Network failed, try cache
      if (error.message === 'Network Error' || !error.response) {
        console.log('Network error: Trying cache for', endpoint);
        
        const cached = await apiCacheDB.get(endpoint, 'GET', config.params);
        if (cached) {
          return { data: cached, fromCache: true };
        }
        
        // For branch stock, return from IndexedDB
        if (endpoint === API_ENDPOINTS.BRANCH_STOCK || endpoint.includes('/branch-stock')) {
          const products = await productsDB.getAll();
          if (products.length > 0) {
            return { data: products, fromCache: true };
          }
        }
      }
      
      throw error;
    }
  }
  
  /**
   * POST request with offline support
   * Note: Sales endpoints are handled directly in Checkout.js, not here
   */
  async post(endpoint, data, config = {}) {
    // If offline, queue the request (for non-sales endpoints)
    if (!navigator.onLine) {
      console.log('Offline: Queuing POST request for', endpoint);
      
      await syncQueueDB.add({
        endpoint,
        method: 'POST',
        payload: data,
        headers: config.headers || {},
      });
      
      return {
        data: {
          id: `temp-${Date.now()}`,
          message: 'Queued for sync when online',
          queued: true,
        },
        queued: true,
      };
    }
    
    try {
      // Try network request
      return await apiClient.post(endpoint, data, config);
    } catch (error) {
      // Network failed, queue the request
      if (error.message === 'Network Error' || !error.response) {
        console.log('Network error: Queuing POST request for', endpoint);
        
        await syncQueueDB.add({
          endpoint,
          method: 'POST',
          payload: data,
          headers: config.headers || {},
        });
        
        return {
          data: {
            id: `temp-${Date.now()}`,
            message: 'Queued for sync when online',
            queued: true,
          },
          queued: true,
        };
      }
      
      throw error;
    }
  }
  
  /**
   * PUT request with offline support
   */
  async put(endpoint, data, config = {}) {
    if (!navigator.onLine) {
      await syncQueueDB.add({
        endpoint,
        method: 'PUT',
        payload: data,
        headers: config.headers || {},
      });
      
      return {
        data: { message: 'Queued for sync when online', queued: true },
        queued: true,
      };
    }
    
    try {
      return await apiClient.put(endpoint, data, config);
    } catch (error) {
      if (error.message === 'Network Error' || !error.response) {
        await syncQueueDB.add({
          endpoint,
          method: 'PUT',
          payload: data,
          headers: config.headers || {},
        });
        
        return {
          data: { message: 'Queued for sync when online', queued: true },
          queued: true,
        };
      }
      throw error;
    }
  }
  
  /**
   * PATCH request with offline support
   */
  async patch(endpoint, data, config = {}) {
    if (!navigator.onLine) {
      await syncQueueDB.add({
        endpoint,
        method: 'PATCH',
        payload: data,
        headers: config.headers || {},
      });
      
      return {
        data: { message: 'Queued for sync when online', queued: true },
        queued: true,
      };
    }
    
    try {
      return await apiClient.patch(endpoint, data, config);
    } catch (error) {
      if (error.message === 'Network Error' || !error.response) {
        await syncQueueDB.add({
          endpoint,
          method: 'PATCH',
          payload: data,
          headers: config.headers || {},
        });
        
        return {
          data: { message: 'Queued for sync when online', queued: true },
          queued: true,
        };
      }
      throw error;
    }
  }
  
  /**
   * DELETE request with offline support
   */
  async delete(endpoint, config = {}) {
    if (!navigator.onLine) {
      await syncQueueDB.add({
        endpoint,
        method: 'DELETE',
        payload: {},
        headers: config.headers || {},
      });
      
      return {
        data: { message: 'Queued for sync when online', queued: true },
        queued: true,
      };
    }
    
    try {
      return await apiClient.delete(endpoint, config);
    } catch (error) {
      if (error.message === 'Network Error' || !error.response) {
        await syncQueueDB.add({
          endpoint,
          method: 'DELETE',
          payload: {},
          headers: config.headers || {},
        });
        
        return {
          data: { message: 'Queued for sync when online', queued: true },
          queued: true,
        };
      }
      throw error;
    }
  }
}

// Create singleton instance
const offlineApi = new OfflineApi();

export default offlineApi;

